import axios from 'axios';
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { usePublicFetch } from '../hooks/usePublicFetch';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

const fmt = (v) => (v || 0).toLocaleString('fr-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtC = (v) => `${fmt(v)} $`;

export default function AjoutPublique() {
  const { token } = useParams();
  const { data, loading, error, refetch } = usePublicFetch(
    `/api/public/ajout/${token}`,
    { notFoundMsg: "Cet ajout n'existe pas ou le lien est invalide." }
  );
  const [showSignature, setShowSignature] = useState(false);
  const [signatureNom, setSignatureNom] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  useEffect(() => {
    if (data?.ajout?.signature_obtenue) setSigned(true);
  }, [data]);

  // Signature canvas handlers
  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = e.clientX ?? e.touches?.[0]?.clientX ?? 0;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };
  const startDrawing = (e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.beginPath(); ctx.moveTo(x, y);
    setIsDrawing(true);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current; if (!canvas) return;
    e.preventDefault();
    const ctx = canvas.getContext('2d');
    const { x, y } = getPos(e, canvas);
    ctx.lineTo(x, y); ctx.stroke();
  };
  const stopDrawing = () => setIsDrawing(false);
  const clearSignature = () => {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = 'white'; ctx.fillRect(0, 0, c.width, c.height);
  };

  useEffect(() => {
    if (showSignature && canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.fillStyle = 'white'; ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.strokeStyle = '#000'; ctx.lineWidth = 2; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    }
  }, [showSignature]);

  const handleSign = async () => {
    if (!signatureNom.trim()) { toast.error('Veuillez entrer votre nom complet'); return; }
    const canvas = canvasRef.current; if (!canvas) return;
    const imageData = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    const hasDrawing = imageData.data.some((p, i) => i % 4 === 3 && p > 0 && (
      imageData.data[i - 1] < 200 || imageData.data[i - 2] < 200 || imageData.data[i - 3] < 200
    ));
    if (!hasDrawing) { toast.error('Veuillez dessiner votre signature'); return; }
    setSigning(true);
    try {
      const res = await axios.post(`${API_URL}/api/public/ajout/${token}/signer`, { 
        signature_data: canvas.toDataURL('image/png'),
        signature_nom: signatureNom.trim()
      });
      if (res.status !== 200) {
        const err = res.data || {};  // 🆕 Phase E2 — fix copy-paste axios (data déjà parsé)
        throw new Error(err.detail || 'Erreur');
      }
      toast.success('Ajout approuvé et signé avec succès !');
      setSigned(true);
      setShowSignature(false);
      refetch();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la signature');
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader2 style={{ width: 40, height: 40, animation: 'spin 1s linear infinite', color: '#2563eb' }} />
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ textAlign: 'center', maxWidth: 400 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Document non trouvé</h2>
        <p style={{ color: '#6b7280' }}>{error}</p>
      </div>
    </div>
  );

  const { ajout, dossier, client, entreprise, soumission, facture_depot } = data || {};
  const montantDepotPaye = facture_depot?.montant_paye_reel || facture_depot?.montant_paye || 0;
  const nouveauSolde = (soumission?.montant_final || 0) + (ajout?.montant_total || 0) - montantDepotPaye;

  return (
    <>
      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @media print {
          .no-print { display: none !important; }
          @page { size: letter; margin: 0.5in; }
          html, body { height: auto !important; overflow: visible !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          #root { position: static !important; overflow: visible !important; height: auto !important; }
          .doc-section-nobreak { break-inside: avoid; page-break-inside: avoid; }
        }
        @media (max-width: 640px) {
          .header-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .client-grid { grid-template-columns: 1fr !important; gap: 12px !important; }
          .doc { margin: 8px !important; }
        }
      `}</style>

      <div style={{ minHeight: '100vh', background: '#f9fafb', fontFamily: "'Segoe UI', Arial, sans-serif", color: '#111', lineHeight: '1.5' }}>

        {/* Barre du haut */}
        <div className="no-print" style={{ background: 'white', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ maxWidth: 850, margin: '0 auto', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Ajout au contrat – Dossier {dossier?.numero_dossier}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {!signed && !showSignature && (
                <button
                  onClick={() => setShowSignature(true)}
                  style={{ padding: '8px 16px', background: '#16a34a', color: 'white', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer' }}
                >
                  ✍️ Approuver et Signer
                </button>
              )}
              <button
                onClick={() => window.print()}
                style={{ padding: '8px 16px', background: '#2563eb', color: 'white', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Printer style={{ width: 15, height: 15 }} /> Imprimer
              </button>
            </div>
          </div>
        </div>

        {/* Bannière signé */}
        {signed && (
          <div className="no-print" style={{ maxWidth: 850, margin: '8px auto 0', padding: '0 16px' }}>
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8, color: '#15803d', fontSize: 13 }}>
              <CheckCircle style={{ width: 18, height: 18, flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>
                Ajout approuvé et signé par {ajout?.signature_nom} le {ajout?.date_signature ? new Date(ajout.date_signature).toLocaleDateString('fr-CA') : ''}
              </span>
            </div>
          </div>
        )}

        {/* Document */}
        <div className="doc" style={{ maxWidth: 850, width: '100%', margin: '12px auto', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <div style={{ padding: 32, fontSize: 12 }}>

            {/* EN-TÊTE */}
            <div className="doc-section" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <div className="header-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: 20, alignItems: 'start' }}>
                <div>
                  {entreprise?.logo_url ? (
                    <img src={entreprise.logo_url} alt="Logo" style={{ width: '100%', maxWidth: 180, height: 'auto', objectFit: 'contain' }} />
                  ) : (
                    <div style={{ width: '100%', height: 80, background: '#f3f4f6', borderRadius: 6 }} />
                  )}
                </div>
                <div style={{ textAlign: 'center' }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{entreprise?.nom_entreprise}</h2>
                  <p>{entreprise?.adresse}</p>
                  <p>{entreprise?.ville} {entreprise?.code_postal}</p>
                  <p style={{ marginTop: 6, fontWeight: 600 }}>{entreprise?.telephone}</p>
                  <p>{entreprise?.email}</p>
                </div>
                <div style={{ fontSize: 11 }}>
                  <p style={{ fontWeight: 700, fontSize: 12 }}>Dossier : {dossier?.numero_dossier}</p>
                  <p style={{ marginTop: 4 }}><strong>Date :</strong> {new Date(ajout?.date_creation).toLocaleDateString('fr-CA')}</p>
                  {dossier?.representant_nom && <p><strong>Représentant :</strong> {dossier.representant_nom}</p>}
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #e5e7eb', fontSize: 10 }}>
                    {entreprise?.numero_tps && <p><strong>TPS :</strong> {entreprise.numero_tps}</p>}
                    {entreprise?.numero_tvq && <p><strong>TVQ :</strong> {entreprise.numero_tvq}</p>}
                    {entreprise?.numero_rbq && <p><strong>RBQ :</strong> {entreprise.numero_rbq}</p>}
                  </div>
                </div>
              </div>

              <h1 style={{ fontSize: 26, fontWeight: 900, textAlign: 'center', margin: '20px 0', letterSpacing: 1 }}>
                AJOUT AU CONTRAT
              </h1>

              {/* Client */}
              <div className="client-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Client :</p>
                  <p style={{ fontWeight: 600 }}>{client?.nom}</p>
                  <p>Téléphone : {client?.telephone}</p>
                  {client?.email && <p>Courriel : {client.email}</p>}
                </div>
                <div>
                  <p style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Adresse des travaux :</p>
                  <p>{dossier?.adresse_travaux}</p>
                  <p>{dossier?.ville_travaux} {dossier?.code_postal_travaux}</p>
                </div>
              </div>
            </div>

            {/* SOUMISSION ORIGINALE (résumé) */}
            {soumission && (
              <div className="doc-section" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <div style={{ borderBottom: '1px solid #e5e7eb', paddingBottom: 8, marginBottom: 12 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>Soumission originale ({soumission.numero_soumission})</h3>
                </div>
                {soumission.items?.length > 0 && (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 8 }}>
                    <tbody>
                      {soumission.items.map((item, i) => (
                        <tr key={item.id || item._id || i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '6px 8px' }}>{item.description}</td>
                          <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600 }}>{fmtC(item.montant)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                <p style={{ textAlign: 'right', fontWeight: 700, fontSize: 13 }}>
                  Total soumission : {fmtC(soumission.montant_final)}
                </p>
              </div>
            )}

            {/* AJOUT AU CONTRAT */}
            <div className="doc-section" style={{ border: '2px solid #d97706', borderRadius: 8, padding: 16, marginBottom: 16, background: '#fffbeb' }}>
              <div style={{ borderBottom: '1px solid #d97706', paddingBottom: 8, marginBottom: 12 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#92400e' }}>Ajout au contrat</h3>
                {ajout?.description && <p style={{ marginTop: 6, fontSize: 12, color: '#78350f' }}>{ajout.description}</p>}
              </div>

              {ajout?.items?.length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #d97706' }}>
                      <th style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 700 }}>Description</th>
                      <th style={{ textAlign: 'right', padding: '8px 10px', fontWeight: 700, width: 140 }}>Montant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ajout.items.map((item, i) => (
                      <tr key={item.id || item._id || i} style={{ borderBottom: '1px solid #fde68a' }}>
                        <td style={{ padding: '7px 10px' }}>{item.description}</td>
                        <td style={{ padding: '7px 10px', textAlign: 'right', fontWeight: 600 }}>{fmtC(item.montant)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div style={{ maxWidth: 320, marginLeft: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #fde68a' }}>
                      <td style={{ padding: '6px 10px', color: '#78350f' }}>Sous-total ajout</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtC(ajout?.sous_total)}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #fde68a' }}>
                      <td style={{ padding: '6px 10px', color: '#78350f' }}>TPS (5%)</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtC(ajout?.tps)}</td>
                    </tr>
                    <tr style={{ borderBottom: '2px solid #92400e' }}>
                      <td style={{ padding: '6px 10px', color: '#78350f' }}>TVQ (9,975%)</td>
                      <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtC(ajout?.tvq)}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', fontWeight: 700, fontSize: 14, color: '#92400e' }}>TOTAL AJOUT</td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: 700, fontSize: 14, color: '#92400e' }}>{fmtC(ajout?.montant_total)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* RÉSUMÉ FINANCIER */}
            <div className="doc-section doc-section-nobreak" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Résumé du solde</h3>
              <div style={{ maxWidth: 360, marginLeft: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <tbody>
                    {soumission && (
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 12px' }}>Soumission originale</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>{fmtC(soumission.montant_final)}</td>
                      </tr>
                    )}
                    <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 12px', color: '#b45309' }}>+ Ajout au contrat</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#b45309' }}>+ {fmtC(ajout?.montant_total)}</td>
                    </tr>
                    {montantDepotPaye > 0 && (
                      <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '8px 12px', color: '#1d4ed8' }}>− Dépôt reçu</td>
                        <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: '#1d4ed8' }}>− {fmtC(montantDepotPaye)}</td>
                      </tr>
                    )}
                    <tr style={{ background: '#f0fdf4' }}>
                      <td style={{ padding: '12px', fontWeight: 800, fontSize: 15, color: '#166534', borderTop: '2px solid #166534' }}>NOUVEAU SOLDE DÛ</td>
                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 800, fontSize: 15, color: '#166534', borderTop: '2px solid #166534' }}>{fmtC(nouveauSolde)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* TERMES ET CONDITIONS */}
            <div className="doc-section doc-section-nobreak" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Termes et conditions</h3>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: 11, lineHeight: 2 }}>
                <li style={{ padding: '3px 0', borderBottom: '1px dotted #e5e7eb' }}>• Tous les prix ci-haut mentionnés sont en dollars Canadiens.</li>
                <li style={{ padding: '3px 0', borderBottom: '1px dotted #e5e7eb' }}>• Paiement net 3 jours.</li>
                <li style={{ padding: '3px 0' }}>• Frais d'administration de 2% par mois sur tout compte passé dû.</li>
              </ul>
            </div>

            {/* SIGNATURE */}
            {!signed ? (
              <div className="doc-section" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>Approbation du client</h3>

                {!showSignature ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ color: '#6b7280', fontSize: 13, marginBottom: 16 }}>
                      En signant, vous approuvez cet ajout au contrat et acceptez les termes et conditions.
                    </p>
                    <button
                      className="no-print"
                      onClick={() => setShowSignature(true)}
                      style={{ padding: '12px 32px', background: '#16a34a', color: 'white', fontSize: 15, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer' }}
                    >
                      ✍️ Approuver et Signer
                    </button>
                  </div>
                ) : (
                  <div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Nom complet *</label>
                      <input
                        type="text"
                        value={signatureNom}
                        onChange={e => setSignatureNom(e.target.value)}
                        placeholder="Entrez votre nom complet"
                        style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label style={{ display: 'block', fontWeight: 600, marginBottom: 6, fontSize: 13 }}>Signature *</label>
                      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>Dessinez votre signature dans le cadre ci-dessous :</p>
                      <canvas
                        ref={canvasRef}
                        width={600}
                        height={160}
                        style={{ border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'crosshair', touchAction: 'none', maxWidth: '100%', display: 'block' }}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={startDrawing}
                        onTouchMove={draw}
                        onTouchEnd={stopDrawing}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <button
                        onClick={clearSignature}
                        style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', fontSize: 13, fontWeight: 500, borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer' }}
                      >
                        Effacer
                      </button>
                      <button
                        onClick={() => setShowSignature(false)}
                        style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', fontSize: 13, fontWeight: 500, borderRadius: 6, border: '1px solid #d1d5db', cursor: 'pointer' }}
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleSign}
                        disabled={signing}
                        style={{ padding: '8px 20px', background: '#16a34a', color: 'white', fontSize: 13, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer', marginLeft: 'auto' }}
                      >
                        {signing ? 'Enregistrement...' : 'Confirmer mon approbation'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="doc-section" style={{ border: '1px solid #86efac', borderRadius: 8, padding: 16, marginBottom: 16, background: '#f0fdf4' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#15803d', marginBottom: 12 }}>
                  <CheckCircle style={{ width: 22, height: 22, flexShrink: 0 }} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>Ajout approuvé et signé</span>
                </div>
                <p style={{ fontSize: 12, color: '#166534' }}>
                  Signé par : <strong>{ajout?.signature_nom}</strong>
                </p>
                <p style={{ fontSize: 12, color: '#166534' }}>
                  Date : {ajout?.date_signature ? new Date(ajout.date_signature).toLocaleDateString('fr-CA') : ''}
                </p>
                {ajout?.signature_data && (
                  <img src={ajout.signature_data} alt="Signature" style={{ maxHeight: 80, marginTop: 8, border: '1px solid #d1fae5', borderRadius: 4 }} />
                )}
              </div>
            )}

            {/* REMERCIEMENTS */}
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, textAlign: 'center', background: '#f0fdf4' }}>
              <p style={{ fontStyle: 'italic', color: '#374151', fontWeight: 600, fontSize: 12 }}>
                Nous vous remercions de la confiance que vous nous accordez pour la réalisation de vos travaux !
              </p>
            </div>

            {/* LOGOS PARTENAIRES */}
            {entreprise?.partenaires_urls?.length > 0 && (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, marginTop: 16, textAlign: 'center' }}>
                <p style={{ fontWeight: 700, marginBottom: 10, fontSize: 12 }}>Nos partenaires</p>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 24, flexWrap: 'wrap' }}>
                  {entreprise.partenaires_urls.map((url, i) => (
                    <img key={url.id || url._id || i} src={url} alt={`Partenaire ${i + 1}`} style={{ height: 45, objectFit: 'contain' }} />
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}
